// shared-lib/cabinet-runs/upperCornerTreatment.js
//
// What the USER says an upper corner holds, and which answers the room allows.
//
// The upper tier decides each corner for itself (`upperTier.planUpperCorners`):
// an L where both legs are clear, else one leg to the wall, else neither. That
// is a rule about what FITS, and more than one thing usually does — an L fits
// wherever a blind corner does, and which leg runs to the wall is a designer's
// call about where the sink is, not a fact about the walls. So the corner
// carries a choice (user request 2026-09-21).
//
// The record rides on the corner's own per-tier override, beside its
// appearance: `{ cornerIndex, tier: 'upper', treatment: { mode, extend? } }`.
// One record per corner per tier already exists for Unit-scope appearance
// (`cornerOverride`), and a second keyed the same way would be two answers to
// "what is at this corner".
//
// A choice is only ever HONOURED when it fits. The lower tier's recorded
// treatment is trusted unchecked because the generator derived it from what it
// had built; a person can pick an L beside a window. So every option is graded
// against the same clear runs the automatic plan reads, the panel offers the
// ones that fit and says why the others do not, and a saved choice the room no
// longer allows falls back to the automatic plan instead of being built into
// an opening.
//
// Pure. No I/O.
const { cornerOverride } = require('./bayOverrides');
const {
  CORNER_OPTIONS,
  userChoiceField,
  optionIdOf,
  treatmentOf,
} = require('./cornerChoice');
const { UPPER_CABINET_DIMENSIONS } = require('../kitchen-assembly/constants');
const {
  upperCornerLegs,
  MIN_UPPER_CORNER_LEG,
} = require('../kitchen-assembly/upperCornerLegs');

const UPPER_DEPTH = UPPER_CABINET_DIMENSIONS.depth;

/** The vocabulary is both tiers' (`cornerChoice.js`); kept under this name
 *  for the callers that already had it. */
const UPPER_CORNER_OPTIONS = CORNER_OPTIONS;

/** The user's recorded treatment for upper corner `cornerIndex`, or null. */
function recordedUpperTreatment(run, cornerIndex) {
  const t = cornerOverride(run, cornerIndex, 'upper')?.[
    userChoiceField('upper')
  ];
  return optionIdOf(t) ? t : null;
}

/**
 * Has the user said NO upper L stands at this corner?
 *
 * A derived run carries the generator's own upper L through a re-solve
 * wherever the fill hangs none (`materializeRun.carryForwardOwned`), and the
 * plan stops its legs at that unit. Both read the run's owned corner cabinets
 * through ONE function for exactly this reason — a plan made around a
 * different set than the carry keeps hangs uppers through a kept L — so the
 * release is decided here, from the record alone, and both obey it.
 */
function releasesCarriedUpper(run, cornerIndex) {
  const t = recordedUpperTreatment(run, cornerIndex);
  return !!t && t.mode !== 'cabinet';
}

/**
 * Grade every option at one corner.
 *
 * @param {Object} m what the plan measured
 * @param {number} m.clearA clear upper-band wall from the vertex along leg A
 * @param {number} m.clearB …along leg B
 * @param {string} [m.blockerA] what ends leg A's clear run (a keep-out `kind`)
 * @param {string} [m.blockerB]
 * @param {string} [m.blockerIdA] that keep-out's opening id, where it is one
 * @param {string} [m.blockerIdB]
 * @param {boolean} [m.oblique] the joint is not a right angle
 * @param {boolean} [m.hasIncumbent] the run already owns an upper L here
 * @returns {{id, mode, extend?, feasible: boolean, reason?: Object}[]} empty at
 *   an oblique joint: its stops are the geometry's, there is nothing to choose
 */
function gradeUpperCorner(m) {
  if (m.oblique) return [];
  const short = (need) => {
    const leg = !(m.clearA >= need) ? 'A' : !(m.clearB >= need) ? 'B' : null;
    if (!leg) return null;
    return {
      code: 'leg-too-short',
      leg,
      need,
      clear: leg === 'A' ? m.clearA : m.clearB,
      blocker: (leg === 'A' ? m.blockerA : m.blockerB) ?? null,
      blockerId: (leg === 'A' ? m.blockerIdA : m.blockerIdB) ?? null,
    };
  };
  return UPPER_CORNER_OPTIONS.map((opt) => {
    let reason = null;
    if (opt.mode === 'cabinet') {
      // The run's own L may stand on legs this rule would refuse; it is there.
      if (!upperCornerLegs(m.clearA, m.clearB) && !m.hasIncumbent)
        reason = short(MIN_UPPER_CORNER_LEG);
    } else if (opt.mode === 'blind') {
      // Either leg may take the corner, but it sweeps an upper's depth of the
      // OTHER wall, so both must be clear for that much.
      reason = short(UPPER_DEPTH);
    }
    return { ...opt, feasible: !reason, ...(reason ? { reason } : {}) };
  });
}

/**
 * The plan a chosen option dictates, in `planUpperCorners`' shape — or null
 * when it is `cabinet` (the caller owns the L and the incumbent) or unknown.
 */
function planFromUpperTreatment(treatment) {
  if (treatment?.mode === 'blind') {
    const extend = treatment.extend === 'B' ? 'B' : 'A';
    return {
      mode: 'blind',
      extend,
      trimA: extend === 'A' ? 0 : UPPER_DEPTH,
      trimB: extend === 'B' ? 0 : UPPER_DEPTH,
    };
  }
  if (treatment?.mode === 'empty')
    return { mode: 'empty', trimA: UPPER_DEPTH, trimB: UPPER_DEPTH };
  return null;
}

module.exports = {
  UPPER_CORNER_OPTIONS,
  optionIdOf,
  treatmentOf,
  recordedUpperTreatment,
  releasesCarriedUpper,
  gradeUpperCorner,
  planFromUpperTreatment,
};
